/** Date helpers shared by the Worker and the app. All pure, all unit-tested. */

import type { IsoDate, MonthKey } from './types';

/** `yyyy-mm-dd` for a Date, in *local* time — these are calendar dates, not instants. */
export function toIsoDate(d: Date): IsoDate {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function today(): IsoDate {
  return toIsoDate(new Date());
}

/** The `yyyy-mm` bucket an ISO date belongs to. Stored, never derived at read time. */
export function toMonthKey(date: IsoDate): MonthKey {
  return date.slice(0, 7);
}

export function isValidIsoDate(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  if (m < 1 || m > 12 || d < 1) return false;
  return d <= daysInMonth(y, m);
}

export function daysInMonth(year: number, month1: number): number {
  return new Date(year, month1, 0).getDate();
}

/** Shift an ISO date by whole days without tripping over DST. */
export function addDays(date: IsoDate, days: number): IsoDate {
  const [y, m, d] = date.split('-').map(Number);
  return toIsoDate(new Date(y, m - 1, d + days));
}

/** First and last day of the month containing `date`. */
export function monthRange(date: IsoDate): { from: IsoDate; to: IsoDate } {
  const [y, m] = date.split('-').map(Number);
  return {
    from: `${date.slice(0, 7)}-01`,
    to: `${date.slice(0, 7)}-${String(daysInMonth(y, m)).padStart(2, '0')}`,
  };
}

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** `2026-03` → `March 2026`. For report headings. */
export function formatMonth(month: MonthKey): string {
  const [y, m] = month.split('-').map(Number);
  return `${MONTH_NAMES[m - 1] ?? month} ${y}`;
}

/** `2026-03-09` → `Mon 9 Mar`. For entry lists. */
export function formatDayShort(date: IsoDate): string {
  const [y, m, d] = date.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const weekday = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dt.getDay()];
  return `${weekday} ${d} ${MONTH_NAMES[m - 1]?.slice(0, 3)}`;
}

/**
 * Round hours to the nearest step (default half-hour, matching how the existing
 * timesheets are kept). Guards against float noise like 2.4999999997.
 */
export function roundHours(hours: number, step = 0.5): number {
  if (step <= 0) return hours;
  return Math.round((hours / step) * (1 + Number.EPSILON)) * step;
}

/** Elapsed milliseconds → decimal hours, rounded to `step`. */
export function msToHours(ms: number, step = 0.5): number {
  return roundHours(ms / 3_600_000, step);
}

/** `3.5` → `3h 30m`, for the timer and entry rows. */
export function formatHours(hours: number): string {
  const total = Math.round(hours * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (!h) return `${m}m`;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Inclusive range test on ISO dates — safe because the format sorts lexically. */
export function inRange(date: IsoDate, from?: IsoDate, to?: IsoDate): boolean {
  if (from && date < from) return false;
  if (to && date > to) return false;
  return true;
}
